/**
 * Symbole der Sicherheits-, Brandschutz- und Elektroanlagen — die 28 Kacheln
 * des Anlagen-Katalogs (`anlagenKatalog.ts`).
 *
 * Machart, Farben und Kommentierung sind aus `bauartSchnitt.tsx` bzw.
 * `ausstattungBilder.tsx` übernommen, nicht neu erfunden: Farben
 * ausschließlich aus `illustrationTokens.ts`, keine `<defs>`/`<pattern>` und
 * damit keine `id`-Attribute — zwei Kacheln desselben Symbols auf einer Seite
 * würden sich sonst über `url(#…)` gegenseitig den Paint-Server wegnehmen.
 *
 * **Gemeinsames Raster.** Alle 28 zeichnen in `0 0 100 100` und tragen
 * dieselbe Grundlinie bei y=88; wo etwas an der Decke hängt, kommt die
 * Deckenlinie bei y=12 dazu. Beides zeichnet `Rahmen` zentral, nicht das
 * einzelne Motiv — so kann eine Kachel das Raster gar nicht verlassen, und in
 * einer Reihe nebeneinander wirken alle gleich groß.
 *
 * **Die Kacheln stehen nebeneinander, also müssen sie sich PAARWEISE
 * unterscheiden.** Bei den ortsfesten Löschanlagen trägt deshalb das
 * **Löschmittel** die Unterscheidung, nicht die Düse:
 *
 * | Anlage      | Leitmerkmal im Bild                                        |
 * | ----------- | ---------------------------------------------------------- |
 * | Pulver      | Punktwolke — viele kleine, gefüllte Körner                  |
 * | Schaum      | Blasen — überlappende Kreise mit Kontur                     |
 * | Gas         | drei Flaschen an einer Sammelleitung, Wolke gestrichelt     |
 * | Stickstoff  | EINE Flasche an einem geschlossenen Behälter, Messfühler    |
 * | Sprühwasser | offene Düsenreihe, Tropfenfächer auf ein Schutzobjekt       |
 * | Funken      | Funkenflug IM Rohr, Funkenmelder davor, Löschdüse dahinter  |
 * | Klein       | genau eine Düse über genau einem Gerät                      |
 *
 * Gas ist gestrichelt, weil es unsichtbar ist — ein gezeichneter Gasstrahl
 * behauptete etwas, das man nicht sieht.
 *
 * **Wo der Unterschied dünn ist — offen gesagt.** Gas-Feuerlöschanlage und
 * Stickstoffanlage sind fachlich beide „Gas aus Flaschen". Ehrlich zeichnen
 * lässt sich nur, was sie verschieden MACHEN: die Gasanlage flutet im
 * Brandfall einen Raum (Flaschenbatterie → Düse → Flamme), die
 * Stickstoffanlage hält einen geschlossenen Behälter dauerhaft inert (eine
 * Flasche → Behälter → Sauerstoff-Messfühler, kein Feuer im Bild). Das
 * Kennzeichen „N₂" wäre der eindeutige Unterschied, ist aber Schrift und keine
 * Zeichnung — der Katalogtext daneben nennt ihn ohnehin.
 *
 * **Zählmerkmale statt Feinheiten:** Rauchwarnmelder = EIN Melder an der
 * Decke, sonst nichts; Brandmeldeanlage = Zentrale plus MEHRERE Melder an
 * einer Meldelinie. Netzersatzanlage und Notstromversorgung (Diesel) zeigen
 * denselben Generatorsatz an denselben Koordinaten — der einzige Unterschied
 * ist der Kraftstofftank samt Leitung, und genau das ist auch fachlich der
 * einzige Unterschied.
 *
 * Die `data-teil`-Angaben benennen die tragenden Bauteile. Sie sind kein
 * Beiwerk für Tests, sondern die Beschriftung der Zeichnung im Markup: der
 * Test prüft daran „Pulver zeigt eine Punktwolke, Schaum nicht", ohne
 * Pfaddaten Zeichen für Zeichen festzunageln — solche Tests brechen bei jeder
 * Feinkorrektur an einer Kurve.
 */
import { ILLU } from "./illustrationTokens";

/* ------------------------------------------------------------------ Raster --- */

/** Kantenlänge der quadratischen viewBox — für alle 28 gleich. */
const KANTE = 100;
/** Fußboden/Geländeoberkante. Steht in jedem Motiv, auch bei Wandgeräten. */
const BODEN = 88;
/** Rohdecke. Nur dort gezeichnet, wo etwas daran hängt. */
const DECKE = 12;

/* --------------------------------------------------------------- Bausteine --- */

const Grundlinie = () => (
  <path
    data-teil="grundlinie"
    d={`M6 ${BODEN}h88`}
    stroke={ILLU.roofStroke}
    strokeWidth={2}
    strokeLinecap="round"
  />
);

/**
 * Decke, wahlweise mit Durchbruch — RWA und maschinelle Entrauchung führen
 * beide durch die Decke, die Linie darf dort nicht durchlaufen.
 */
const Deckenlinie = ({ luecke }: { luecke?: readonly [number, number] }) => (
  <path
    data-teil="deckenlinie"
    d={
      luecke
        ? `M6 ${DECKE}h${luecke[0] - 6}M${luecke[1]} ${DECKE}h${94 - luecke[1]}`
        : `M6 ${DECKE}h88`
    }
    stroke={ILLU.roofStroke}
    strokeWidth={2}
    strokeLinecap="round"
  />
);

/** Wandscheibe im Schnitt, mit Mauerfugen — der ruhende Teil der Bauteil-Motive. */
const Wandscheibe = ({ x, y, w, h }: { x: number; y: number; w: number; h: number }) => {
  const fugen: string[] = [];
  for (let fy = y + 9; fy < y + h - 2; fy += 9) fugen.push(`M${x} ${fy}h${w}`);
  return (
    <g data-teil="wandscheibe">
      <rect x={x} y={y} width={w} height={h} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d={fugen.join("")} stroke={ILLU.jointStroke} strokeWidth={0.9} />
    </g>
  );
};

/** Deckenmelder: flacher, nach unten schmaler werdender Körper mit Leuchtpunkt. */
const Melder = ({ x, y = DECKE, breite = 9 }: { x: number; y?: number; breite?: number }) => (
  <g data-teil="melder" transform={`translate(${x} ${y})`}>
    <path
      d={`M-${breite} 0h${breite * 2}l-3 7h-${breite * 2 - 6}Z`}
      fill={ILLU.wallFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.3}
      strokeLinejoin="round"
    />
    <circle cy={4.4} r={1.6} fill={ILLU.sockelFill} />
  </g>
);

/** Gasflasche: Zylinder mit gewölbter Schulter und Ventilblock, stehend auf dem Boden. */
const Flasche = ({
  x,
  breite = 7,
  hoehe = 34,
  teil = "flasche",
}: {
  x: number;
  breite?: number;
  hoehe?: number;
  teil?: string;
}) => (
  <g data-teil={teil} transform={`translate(${x} ${BODEN})`}>
    <path
      d={`M-${breite} 0v-${hoehe - breite}a${breite} ${breite} 0 0 1 ${breite * 2} 0V0Z`}
      fill={ILLU.plateFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.4}
      strokeLinejoin="round"
    />
    <path d={`M-${breite} -9h${breite * 2}`} stroke={ILLU.jointStroke} strokeWidth={1} />
    <rect
      x={-2.6}
      y={-hoehe - 5}
      width={5.2}
      height={5}
      fill={ILLU.steelFill}
      stroke={ILLU.wallStroke}
      strokeWidth={0.9}
    />
  </g>
);

/** Löschdüse: Stutzen mit sich verjüngendem Mundstück, Mündung nach unten. */
const Duese = ({ x, y, teil = "loeschduese" }: { x: number; y: number; teil?: string }) => (
  <g data-teil={teil} transform={`translate(${x} ${y})`}>
    <rect x={-4.5} y={-3} width={9} height={3} fill={ILLU.steelFill} />
    <path
      d="M-4.5 0h9l-3 5h-3Z"
      fill={ILLU.steelFill}
      stroke={ILLU.wallStroke}
      strokeWidth={0.8}
      strokeLinejoin="round"
    />
  </g>
);

/** Flamme: Brandfall im Bild. Gold ist im Formular bereits die Hinweisfarbe. */
const Flamme = ({ x, y = BODEN, hoehe = 22 }: { x: number; y?: number; hoehe?: number }) => (
  <g data-teil="flamme" transform={`translate(${x} ${y}) scale(${(hoehe / 22).toFixed(3)})`}>
    <path
      d="M0 -22C7 -14 11 -9 11 -4A11 11 0 0 1 -11 -4C-11 -10 -3 -13 0 -22Z"
      fill={ILLU.thatchFill}
      stroke={ILLU.thatchTexture}
      strokeWidth={1.4}
      strokeLinejoin="round"
    />
    <path
      d="M0 -11C4 -7 6 -5 6 -2A6 6 0 0 1 -6 -2C-6 -6 -2 -8 0 -11Z"
      fill={ILLU.gefachFill}
      stroke={ILLU.thatchTexture}
      strokeWidth={0.8}
    />
  </g>
);

/** Rauchfahne: drei Schwünge, von unten nach oben aufsteigend. */
const Rauchfaden = ({ x, y, hoehe }: { x: number; y: number; hoehe: number }) => {
  const s = (hoehe / 3).toFixed(1);
  return (
    <path
      data-teil="rauchfaden"
      d={`M${x} ${y}q-7 -${(hoehe / 6).toFixed(1)} 0 -${s}t0 -${s}t0 -${s}`}
      stroke={ILLU.jointStroke}
      strokeWidth={2.4}
      strokeLinecap="round"
      fill="none"
    />
  );
};

/** Funke: achtzackiger Stern — bewusst kein Kreis, sonst läse er sich als Tropfen. */
const Funke = ({ x, y, r = 3 }: { x: number; y: number; r?: number }) => {
  const k = (r * 0.32).toFixed(2);
  return (
    <path
      data-teil="funke"
      transform={`translate(${x} ${y})`}
      d={`M0 -${r}L${k} -${k}L${r} 0L${k} ${k}L0 ${r}L-${k} ${k}L-${r} 0L-${k} -${k}Z`}
      fill={ILLU.thatchFill}
      stroke={ILLU.thatchTexture}
      strokeWidth={0.6}
      strokeLinejoin="round"
    />
  );
};

/** Wassertropfen — Spitze oben, Bauch unten. */
const Tropfen = ({ x, y, hoehe = 10 }: { x: number; y: number; hoehe?: number }) => (
  <path
    data-teil="tropfen"
    transform={`translate(${x} ${y})`}
    d={`M0 0c-3.4 -3.4 -3.4 -6 0 -${hoehe}c3.4 4 3.4 6.6 0 ${hoehe}Z`}
    fill={ILLU.glassFill}
    stroke={ILLU.wallStroke}
    strokeWidth={0.9}
  />
);

/** Pulverkorn: gefüllt und ohne Kontur — das trennt die Wolke von den Schaumblasen. */
const Punkt = ({ x, y, r }: { x: number; y: number; r: number }) => (
  <circle data-teil="punkt" cx={x} cy={y} r={r} fill={ILLU.sockelFill} />
);

/** Schaumblase: Kreis MIT Kontur — großblasig, nicht körnig. */
const Blase = ({ x, y, r }: { x: number; y: number; r: number }) => (
  <circle data-teil="blase" cx={x} cy={y} r={r} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
);

/* ----------------------------------------------------- Motive: Hauptanlagen --- */

/**
 * Brandmeldeanlage: Zentrale an der Wand, zwei Melder an der Decke, dazwischen
 * die Meldelinie. Das Gegenstück zum Rauchwarnmelder ist bewusst ein
 * ZÄHLmerkmal — dort ein Melder ohne alles, hier Zentrale plus mehrere Melder.
 */
const Bma = () => (
  <>
    <Deckenlinie />
    <Melder x={38} />
    <Melder x={70} />
    <path
      data-teil="meldelinie"
      d="M70 19v8H29v17M38 19v8"
      stroke={ILLU.steelFill}
      strokeWidth={1.8}
      fill="none"
      strokeLinecap="round"
    />
    <g data-teil="zentrale">
      <rect x={14} y={44} width={30} height={44} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
      <rect x={18} y={48} width={22} height={13} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <path d="M21 53h16M21 57h10" stroke={ILLU.jointStroke} strokeWidth={1} />
      <circle cx={21} cy={68} r={2.4} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={0.8} />
      <circle cx={29} cy={68} r={2.4} fill={ILLU.sockelFill} />
      <circle cx={37} cy={68} r={2.4} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
      <path d="M18 76h22M18 82h22" stroke={ILLU.jointStroke} strokeWidth={1.4} />
    </g>
  </>
);

/**
 * Einbruchmeldeanlage: Fenster mit Öffnungskontakt, Bewegungsmelder in der
 * Ecke mit Erfassungskegel, Außensirene. Keine Zentrale — sonst wäre sie von
 * der Brandmeldeanlage nicht zu unterscheiden.
 */
const Ema = () => (
  <>
    <g data-teil="sirene">
      <rect x={8} y={20} width={6} height={10} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <path d="M14 20 24 14v22l-10-6Z" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} strokeLinejoin="round" />
      <path d="M28 19q5 6 0 12M33 15q9 10 0 20" stroke={ILLU.jointStroke} strokeWidth={1.6} fill="none" strokeLinecap="round" />
    </g>
    <g data-teil="fenster">
      <rect x={10} y={46} width={30} height={26} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M25 46v26M10 59h30" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
    <g data-teil="fensterkontakt">
      <rect x={19} y={41} width={5} height={5} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
      <rect x={26} y={41} width={5} height={5} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
    </g>
    <path
      data-teil="erfassungskegel"
      d="M80 28 54 76h44Z"
      fill={ILLU.glassFill}
      stroke={ILLU.jointStroke}
      strokeWidth={1}
      strokeDasharray="3 3"
    />
    <path d="M66 56q14 4 26 0M62 66q18 5 34 0" stroke={ILLU.jointStroke} strokeWidth={0.9} fill="none" />
    <g data-teil="bewegungsmelder">
      <rect x={72} y={16} width={16} height={12} rx={2.5} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <circle cx={80} cy={23} r={3} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    </g>
  </>
);

/**
 * Rauch- und Wärmeabzugsanlage: Öffnung in der Dachfläche, die Klappe steht
 * aufgestellt darüber, der Rauch zieht von selbst hinaus. NATÜRLICHER Abzug —
 * kein Ventilator; das ist der ganze Unterschied zur maschinellen Entrauchung.
 */
const Rwa = () => (
  <>
    <g data-teil="dachflaeche">
      <rect x={6} y={26} width={38} height={10} fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth={1.5} />
      <rect x={64} y={26} width={30} height={10} fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth={1.5} />
    </g>
    <Rauchfaden x={50} y={80} hoehe={54} />
    <Rauchfaden x={60} y={84} hoehe={62} />
    <path
      data-teil="dachklappe"
      d="M44 26 59 5 54 2 39 23Z"
      fill={ILLU.plateFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.4}
      strokeLinejoin="round"
    />
    <circle data-teil="scharnier" cx={44} cy={26} r={2.4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    <Flamme x={44} hoehe={18} />
  </>
);

/**
 * Sprinkleranlage: Rohr unter der Decke mit drei Köpfen. Zwei tragen ihre
 * Glasampulle noch, der mittlere hat ausgelöst und sprüht — genau DAS trennt
 * sie von der Sprühwasseranlage, deren Düsen offen sind und alle zugleich
 * fluten.
 */
const Sprinkler = () => {
  const kopf = (x: number, mitAmpulle: boolean) => (
    <g data-teil="sprinklerkopf" transform={`translate(${x} 22)`} key={x}>
      <rect x={-4} y={0} width={8} height={6} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
      {mitAmpulle && (
        <rect
          data-teil="ampulle"
          x={-1.3}
          y={6}
          width={2.6}
          height={7}
          fill={ILLU.thatchFill}
          stroke={ILLU.thatchTexture}
          strokeWidth={0.8}
        />
      )}
      <path d="M-6 14h12" stroke={ILLU.steelFill} strokeWidth={2.4} strokeLinecap="round" />
    </g>
  );
  return (
    <>
      <Deckenlinie />
      <path data-teil="rohrleitung" d="M12 20h76" stroke={ILLU.steelFill} strokeWidth={5} strokeLinecap="round" />
      <path d="M24 17v6M60 17v6" stroke={ILLU.jointStroke} strokeWidth={1} />
      {kopf(28, true)}
      {kopf(72, true)}
      <g data-teil="wasserkegel">
        <path
          d="M50 38 34 70M50 38 42 74M50 38 50 76M50 38 58 74M50 38 66 70"
          stroke={ILLU.jointStroke}
          strokeWidth={1.2}
          strokeLinecap="round"
        />
        <Tropfen x={38} y={78} hoehe={8} />
        <Tropfen x={50} y={84} hoehe={8} />
        <Tropfen x={62} y={78} hoehe={8} />
      </g>
      {kopf(50, false)}
    </>
  );
};

/**
 * Blitzschutzanlage: Fangstange über dem First, Ableitung an der Gebäudekante,
 * Erder unter der Geländeoberkante. Der Blitz schlägt in die Fangstange, nicht
 * ins Dach — das ist die Aussage der Anlage.
 */
const Blitzschutz = () => (
  <>
    <path d="M18 56 50 34 82 56Z" fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth={1.5} strokeLinejoin="round" />
    <rect x={18} y={56} width={64} height={32} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <path d="M18 68h64M18 78h64" stroke={ILLU.jointStroke} strokeWidth={0.9} />
    <path data-teil="fangstange" d="M50 34V22" stroke={ILLU.steelFill} strokeWidth={3.2} strokeLinecap="round" />
    <path data-teil="ableitung" d="M50 34 82 56v32" stroke={ILLU.steelFill} strokeWidth={2.4} fill="none" strokeLinejoin="round" />
    <g data-teil="erder">
      <path d="M82 88v8" stroke={ILLU.steelFill} strokeWidth={2.4} />
      <path d="M68 96h26" stroke={ILLU.steelFill} strokeWidth={3.2} strokeLinecap="round" />
      <path d="M64 92l5-4M74 92l5-4M84 92l5-4" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
    <path
      data-teil="blitz"
      // Klassischer Zickzack, Spitze unten auf der Fangstange (50/22) — der
      // Einschlag trifft die Stange, nicht das Dach. Das ist die Aussage der
      // Anlage, und ein Blitz irgendwo im Bild hätte sie nicht.
      d="M13 2 3 14h7l-2 8 10-12h-7l4-8z"
      transform="translate(42 0)"
      fill={ILLU.thatchFill}
      stroke={ILLU.thatchTexture}
      strokeWidth={1.1}
      strokeLinejoin="round"
    />
  </>
);

/* ---------------------------------------------------- Motive: Feuer, Brand --- */

/**
 * Rauchwarnmelder: EIN Melder an der Decke, der Rauch steigt ihm entgegen, er
 * schlägt akustisch Alarm. Keine Zentrale, keine Leitung — der Melder ist die
 * ganze Anlage. Genau darin liegt der Unterschied zur Brandmeldeanlage.
 */
const Rauchwarnmelder = () => (
  <>
    <Deckenlinie />
    <Melder x={50} breite={12} />
    <path
      data-teil="schallwellen"
      d="M32 22q-7 6 0 13M25 17q-12 11 0 23M68 22q7 6 0 13M75 17q12 11 0 23"
      stroke={ILLU.jointStroke}
      strokeWidth={1.8}
      fill="none"
      strokeLinecap="round"
    />
    <Rauchfaden x={46} y={84} hoehe={48} />
  </>
);

/**
 * Feuerlöscher: tragbarer Behälter im Wandhalter, mit Griffhebel, Manometer
 * und Schlauch. Handgerät statt Installation — deshalb der Halter, an dem er
 * hängt.
 */
const Feuerloescher = () => (
  <>
    <g data-teil="wandhalter">
      <rect x={18} y={40} width={6} height={30} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <path d="M24 46h12M24 64h12" stroke={ILLU.sockelFill} strokeWidth={3} strokeLinecap="round" />
    </g>
    <path
      data-teil="loescherbehaelter"
      d="M36 44h20v39a5 5 0 0 1-5 5H41a5 5 0 0 1-5-5Z"
      fill={ILLU.thatchFill}
      stroke={ILLU.thatchTexture}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    <rect x={39} y={58} width={14} height={12} fill={ILLU.gefachFill} stroke={ILLU.thatchTexture} strokeWidth={1} />
    <path d="M42 63h8M42 67h5" stroke={ILLU.thatchTexture} strokeWidth={0.9} />
    <rect x={42} y={36} width={8} height={8} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    <path data-teil="griffhebel" d="M40 34h16" stroke={ILLU.steelFill} strokeWidth={3} strokeLinecap="round" />
    <g data-teil="manometer">
      <circle cx={60} cy={38} r={5} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M60 38 63 35" stroke={ILLU.wallStroke} strokeWidth={1.1} strokeLinecap="round" />
      <path d="M56 42 52 45" stroke={ILLU.steelFill} strokeWidth={1.6} />
    </g>
    <path
      data-teil="schlauch"
      d="M56 48q16 6 14 20t-8 16"
      stroke={ILLU.roofStroke}
      strokeWidth={2.4}
      fill="none"
      strokeLinecap="round"
    />
    <path d="M58 84h8l-4 4Z" fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} strokeLinejoin="round" />
  </>
);

/**
 * Brandschutztür: GESCHLOSSENES Türblatt in der Zarge, mit Drücker und
 * obenliegendem Türschließer. Der Schließer ist das Bauteil, das sie zur
 * Brandschutztür macht — und zugleich die Abgrenzung zur Feststellanlage, die
 * dieselbe Tür OFFEN hält.
 */
const Brandschutztueren = () => (
  <>
    <Wandscheibe x={8} y={20} w={20} h={68} />
    <Wandscheibe x={72} y={20} w={20} h={68} />
    <rect data-teil="zarge" x={28} y={20} width={44} height={68} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <rect data-teil="tuerblatt" x={33} y={25} width={34} height={63} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <rect x={38} y={44} width={24} height={26} fill="none" stroke={ILLU.jointStroke} strokeWidth={1} />
    <g data-teil="tuerdruecker">
      <rect x={35} y={56} width={4} height={5} fill={ILLU.steelFill} />
      <path d="M37 58h-6" stroke={ILLU.steelFill} strokeWidth={2.4} strokeLinecap="round" />
    </g>
    <g data-teil="tuerschliesser">
      <rect x={44} y={28} width={18} height={7} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <path d="M62 31 76 37" stroke={ILLU.steelFill} strokeWidth={2.2} strokeLinecap="round" />
      <circle cx={76} cy={37} r={2.2} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
    </g>
  </>
);

/**
 * Wandhydrant: Schrank mit Sichtscheibe, darin die Schlauchhaspel mit
 * Absperrventil. Löschwasser aus der Wand — im Unterschied zur Steigleitung,
 * die nur das Rohr ohne Schlauch zeigt.
 */
const Wandhydranten = () => (
  <>
    <rect data-teil="hydrantenschrank" x={22} y={24} width={56} height={52} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <rect x={26} y={28} width={48} height={44} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
    <g data-teil="schlauchhaspel">
      <circle cx={50} cy={48} r={16} fill="none" stroke={ILLU.roofStroke} strokeWidth={3} />
      <circle cx={50} cy={48} r={11.5} fill="none" stroke={ILLU.jointStroke} strokeWidth={1.6} />
      <circle cx={50} cy={48} r={7} fill="none" stroke={ILLU.jointStroke} strokeWidth={1.6} />
      <circle cx={50} cy={48} r={3} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
    </g>
    <path
      data-teil="schlauchende"
      d="M64 58q10 8 8 18"
      stroke={ILLU.roofStroke}
      strokeWidth={2.4}
      fill="none"
      strokeLinecap="round"
    />
    <rect x={68} y={76} width={8} height={6} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
    <g data-teil="ventil">
      <path d="M30 76v12" stroke={ILLU.steelFill} strokeWidth={3} />
      <circle cx={30} cy={72} r={4} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M23 72h14" stroke={ILLU.steelFill} strokeWidth={2} strokeLinecap="round" />
    </g>
  </>
);

/**
 * Funkenlöschanlage: sie sitzt IM Absaugrohr, nicht im Raum. Links fliegen die
 * Funken heran, in der Mitte sieht sie der Funkenmelder, rechts löscht die
 * Düse sie noch im Rohr ab — die Reihenfolge ist die Anlage.
 */
const Funkenloeschanlagen = () => (
  <>
    <rect data-teil="rohr" x={6} y={36} width={88} height={26} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <path d="M10 58h16l-4-3M26 58l-4 3" stroke={ILLU.jointStroke} strokeWidth={1.2} fill="none" strokeLinecap="round" />
    <g data-teil="funkenflug">
      <Funke x={14} y={44} r={3} />
      <Funke x={22} y={52} r={2.4} />
      <Funke x={30} y={42} r={2.8} />
      <Funke x={38} y={50} r={3.2} />
      <Funke x={46} y={45} r={2.4} />
    </g>
    <g data-teil="funkenmelder">
      <rect x={50} y={24} width={14} height={12} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <circle cx={57} cy={30} r={3} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M57 36v6" stroke={ILLU.jointStroke} strokeWidth={1} strokeDasharray="2 2" />
    </g>
    <path d="M78 36V22" stroke={ILLU.steelFill} strokeWidth={3} />
    <Duese x={78} y={36} />
    <g data-teil="spruehkeil">
      <path d="M78 42 70 60M78 42 78 60M78 42 86 60" stroke={ILLU.jointStroke} strokeWidth={1.2} strokeLinecap="round" />
      <Tropfen x={74} y={58} hoehe={7} />
      <Tropfen x={83} y={58} hoehe={7} />
    </g>
  </>
);

/**
 * Gas-Feuerlöschanlage: Flaschenbatterie am Sammelrohr, Steigleitung zur Düse,
 * darunter der Brand, der geflutet wird. Das Gas selbst ist gestrichelt —
 * es ist unsichtbar, und eine durchgezogene Wolke wäre eine Erfindung.
 */
const Gasfeuerloeschanlagen = () => (
  <>
    <Flasche x={16} hoehe={34} />
    <Flasche x={32} hoehe={34} />
    <Flasche x={48} hoehe={34} />
    <path
      data-teil="sammelrohr"
      d="M16 49v-6M32 49v-6M48 49v-6M14 43h52v-19h10"
      stroke={ILLU.steelFill}
      strokeWidth={3}
      fill="none"
      strokeLinejoin="round"
    />
    <Duese x={78} y={24} />
    <g data-teil="gaswolke">
      <path
        d="M78 32 64 62M78 32 78 66M78 32 92 62"
        stroke={ILLU.jointStroke}
        strokeWidth={1.4}
        strokeDasharray="4 4"
        strokeLinecap="round"
      />
      <path d="M66 70q6-5 12 0t12 0" stroke={ILLU.jointStroke} strokeWidth={1.2} strokeDasharray="4 4" fill="none" />
    </g>
    <Flamme x={78} hoehe={20} />
  </>
);

/**
 * Stickstoffanlage: EINE Flasche speist über ein Absperrventil einen
 * geschlossenen Behälter, ein Sauerstoff-Messfühler überwacht ihn. Kein Feuer
 * im Bild — inertisiert wird dauerhaft, nicht erst im Brandfall. Das ist der
 * ehrliche Unterschied zur Gas-Feuerlöschanlage (siehe Dateikopf).
 */
const Stickstoffanlage = () => (
  <>
    <Flasche x={18} breite={9} hoehe={44} />
    <path d="M18 39V28h38v10" stroke={ILLU.steelFill} strokeWidth={3} fill="none" strokeLinejoin="round" />
    <g data-teil="absperrventil">
      <circle cx={38} cy={28} r={4} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M38 20v6" stroke={ILLU.steelFill} strokeWidth={2} />
      <path d="M33 20h10" stroke={ILLU.steelFill} strokeWidth={2} strokeLinecap="round" />
    </g>
    <path
      data-teil="inertbehaelter"
      d="M48 46a20 12 0 0 1 38 0v42H48Z"
      fill={ILLU.wallFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
    <path
      data-teil="inertgas"
      d="M54 58h26M52 68h30M54 78h26"
      stroke={ILLU.jointStroke}
      strokeWidth={1.4}
      strokeDasharray="4 4"
    />
    <g data-teil="messfuehler">
      <path d="M86 60h-8" stroke={ILLU.steelFill} strokeWidth={2.2} />
      <rect x={86} y={52} width={10} height={16} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <circle cx={91} cy={60} r={3} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    </g>
  </>
);

/**
 * Kleinlöschanlage: ein kleiner Löschmittelbehälter, EINE Düse, EIN geschütztes
 * Gerät — objektbezogen statt raumdeckend. Der kurze Wirkbereich endet über
 * dem Gerät; das ist der Unterschied zu allen anderen Löschanlagen im Satz.
 */
const Kleinloeschanlagen = () => (
  <>
    <Flasche x={20} breite={6} hoehe={26} teil="kleinbehaelter" />
    <path d="M20 57V34h44" stroke={ILLU.steelFill} strokeWidth={2.6} fill="none" strokeLinejoin="round" />
    <Duese x={64} y={34} teil="einzelduese" />
    <path
      data-teil="wirkbereich"
      d="M64 41 56 50M64 41 64 51M64 41 72 50"
      stroke={ILLU.jointStroke}
      strokeWidth={1.2}
      strokeLinecap="round"
    />
    <g data-teil="objektgeraet">
      <rect x={44} y={54} width={42} height={34} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
      <rect x={56} y={50} width={22} height={5} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <rect x={50} y={62} width={14} height={9} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      <circle cx={74} cy={66} r={3.4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M50 80h30" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    </g>
  </>
);

/**
 * Schaum quillt und legt sich als Decke aufs Brandgut — deshalb wandern die
 * Blasen vom Schaumrohr nach unten und häufen sich am Boden. Feste Liste statt
 * Zufall: die Zeichnung muss bei jedem Rendern dieselbe sein.
 */
const SCHAUM_BLASEN: readonly { x: number; y: number; r: number }[] = [
  { x: 72, y: 44, r: 3.4 },
  { x: 80, y: 50, r: 4.6 },
  { x: 68, y: 54, r: 4 },
  { x: 78, y: 62, r: 5.4 },
  { x: 64, y: 66, r: 4.4 },
  { x: 88, y: 60, r: 3.6 },
  { x: 86, y: 70, r: 5 },
  { x: 70, y: 76, r: 5.6 },
  { x: 58, y: 78, r: 4.2 },
  { x: 82, y: 80, r: 4.8 },
  { x: 92, y: 78, r: 3.4 },
];

/**
 * Ortsfeste Schaumlöschanlage: Schaummittelbehälter, Zumischer in der
 * Wasserleitung, Schaumrohr — und als Löschmittel großblasiger Schaum. Die
 * Blasen sind das, was diese Anlage von der Pulveranlage trennt.
 */
const Schaumloeschanlagen = () => (
  <>
    <path
      data-teil="schaummittelbehaelter"
      d="M10 52a12 6 0 0 1 24 0v36H10Z"
      fill={ILLU.plateFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
    <rect x={14} y={58} width={4} height={24} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    <path d="M14 70h4" stroke={ILLU.wallStroke} strokeWidth={1.2} />
    <path d="M22 46v-6h18M52 40h14v-8" stroke={ILLU.steelFill} strokeWidth={3} fill="none" strokeLinejoin="round" />
    <g data-teil="zumischer">
      <rect x={40} y={34} width={12} height={12} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
      <path d="M46 34v-6" stroke={ILLU.steelFill} strokeWidth={2.2} />
      <path d="M42 28h8" stroke={ILLU.steelFill} strokeWidth={2} strokeLinecap="round" />
    </g>
    <g data-teil="schaumrohr">
      <rect x={60} y={26} width={14} height={6} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      <path d="M74 26h8l-4 10Z" fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} strokeLinejoin="round" />
    </g>
    <g data-teil="schaumteppich">
      {SCHAUM_BLASEN.map((b) => (
        <Blase key={`${b.x}-${b.y}`} x={b.x} y={b.y} r={b.r} />
      ))}
    </g>
  </>
);

/**
 * Sprühwasserlöschanlage: OFFENE Düsen an einem Rohr, alle fluten zugleich und
 * legen einen Wasservorhang über ein Schutzobjekt. Keine Ampulle an den Düsen —
 * genau das unterscheidet sie von der Sprinkleranlage, bei der jeder Kopf
 * einzeln auslöst.
 */
const Spruehwasserloeschanlagen = () => {
  const faecher = [24, 41, 58, 75];
  return (
    <>
      <Deckenlinie />
      <path data-teil="rohr" d="M12 20h76" stroke={ILLU.steelFill} strokeWidth={5} strokeLinecap="round" />
      {faecher.map((x) => (
        <g key={x}>
          <Duese x={x} y={24} teil="offeneduese" />
          <g data-teil="tropfenfaecher">
            <path
              d={`M${x} 30 ${x - 8} 52M${x} 30 ${x} 54M${x} 30 ${x + 8} 52`}
              stroke={ILLU.jointStroke}
              strokeWidth={1.2}
              strokeLinecap="round"
            />
            <Tropfen x={x - 5} y={62} hoehe={8} />
            <Tropfen x={x + 4} y={68} hoehe={8} />
          </g>
        </g>
      ))}
      <g data-teil="schutzobjekt">
        <rect x={22} y={72} width={56} height={16} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
        <path d="M32 72v16M42 72v16M52 72v16M62 72v16M72 72v16" stroke={ILLU.wallStroke} strokeWidth={1} />
      </g>
    </>
  );
};

/**
 * Maschinelle Entrauchungsanlage: der Rauch wird von einem Ventilator durch
 * den Kanal ins Freie GEZOGEN. Ventilator und Motor sind das Merkmal — die RWA
 * daneben zeigt dieselbe Aufgabe ohne Maschine, nur mit aufgestellter Klappe.
 */
const Entrauchungsanlagen = () => (
  <>
    <Deckenlinie luecke={[38, 64]} />
    <rect data-teil="kanal" x={38} y={4} width={26} height={52} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <Rauchfaden x={30} y={78} hoehe={30} />
    <path
      data-teil="rauchfaden"
      d="M34 48q10-6 17-8"
      stroke={ILLU.jointStroke}
      strokeWidth={2.4}
      fill="none"
      strokeLinecap="round"
    />
    <Rauchfaden x={51} y={30} hoehe={24} />
    <g data-teil="ventilator">
      <circle cx={51} cy={38} r={11} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      {[0, 90, 180, 270].map((winkel) => (
        <path
          key={winkel}
          data-teil="fluegel"
          transform={`rotate(${winkel} 51 38)`}
          d="M51 35q9-4 11 3q-9 5-11-3Z"
          fill={ILLU.plateFill}
          stroke={ILLU.wallStroke}
          strokeWidth={1}
          strokeLinejoin="round"
        />
      ))}
      <circle cx={51} cy={38} r={2.6} fill={ILLU.steelFill} />
    </g>
    <g data-teil="motor">
      <rect x={64} y={32} width={12} height={13} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
      <path d="M64 38h-2" stroke={ILLU.steelFill} strokeWidth={2} />
      <path d="M67 32v-4M73 32v-4" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
    <Flamme x={30} hoehe={18} />
  </>
);

/**
 * Förderanlagenabschluss: das Band läuft durch die Wand, über der Öffnung
 * hängt das Abschlussblatt in seinen Führungen und fährt im Brandfall herab.
 * Das Band ist das Bauteil, das diese Öffnung von jeder anderen unterscheidet.
 */
const Foerderanlagenabschluesse = () => (
  <>
    <Wandscheibe x={42} y={6} w={20} h={32} />
    <Wandscheibe x={42} y={74} w={20} h={14} />
    <rect
      data-teil="wanddurchbruch"
      x={42}
      y={38}
      width={20}
      height={36}
      fill="none"
      stroke={ILLU.jointStroke}
      strokeWidth={1}
      strokeDasharray="3 3"
    />
    <path
      data-teil="foerderband"
      d="M18 58h68a8 8 0 0 1 0 16H18a8 8 0 0 1 0-16Z"
      fill="none"
      stroke={ILLU.roofStroke}
      strokeWidth={2.5}
      strokeLinejoin="round"
    />
    <circle data-teil="umlenkrolle" cx={18} cy={66} r={8} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <circle data-teil="umlenkrolle" cx={86} cy={66} r={8} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <circle cx={18} cy={66} r={2} fill={ILLU.steelFill} />
    <circle cx={86} cy={66} r={2} fill={ILLU.steelFill} />
    <rect data-teil="foerdergut" x={26} y={47} width={14} height={11} fill={ILLU.timberFill} stroke={ILLU.timberStroke} strokeWidth={1.2} />
    <path d="M26 52h14" stroke={ILLU.timberStroke} strokeWidth={0.9} />
    <path data-teil="fuehrungsschiene" d="M39 32v30M65 32v30" stroke={ILLU.steelFill} strokeWidth={2.4} strokeLinecap="round" />
    <g data-teil="abschlussblatt">
      <rect x={39} y={38} width={26} height={16} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M39 43h26M39 48h26" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
  </>
);

/**
 * Brandschutzklappe: sie steckt IM Lüftungskanal, dort wo er die Wand
 * durchdringt. Klappenblatt auf seiner Drehachse, darüber der Auslöser auf dem
 * Gehäuse. Kein Türblatt, kein Rahmen — der Kanal ist das Umfeld.
 */
const Brandschutzklappen = () => (
  <>
    <rect data-teil="lueftungskanal" x={6} y={36} width={88} height={26} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <Wandscheibe x={40} y={8} w={18} h={28} />
    <Wandscheibe x={40} y={62} w={18} h={26} />
    <rect data-teil="klappengehaeuse" x={40} y={36} width={18} height={26} fill="none" stroke={ILLU.wallStroke} strokeWidth={2.2} />
    <path d="M12 49h16l-4-3M28 49l-4 3" stroke={ILLU.jointStroke} strokeWidth={1.2} fill="none" strokeLinecap="round" />
    <g transform="rotate(38 49 49)">
      <rect data-teil="klappenblatt" x={39} y={46.5} width={20} height={5} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    </g>
    <circle data-teil="drehachse" cx={49} cy={49} r={2.8} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    <g data-teil="ausloeser">
      <rect x={41} y={22} width={16} height={14} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <path d="M45 26h8M45 30h8" stroke={ILLU.jointStroke} strokeWidth={1.1} />
      <circle cx={49} cy={19} r={2.6} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={0.9} />
    </g>
  </>
);

/**
 * Brandschottung: die Wanddurchführung selbst ist das Bauteil — die Öffnung
 * ist mit Schottmasse verschlossen, durch die die Kabel laufen. Kein
 * bewegliches Teil, deshalb auch kein Auslöser: ein Schott schließt nicht, es
 * IST zu.
 */
const Brandschottungen = () => (
  <>
    <Wandscheibe x={38} y={6} w={24} h={82} />
    <g data-teil="schottmasse">
      <rect x={38} y={38} width={24} height={28} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
      <path
        d="M40 66 62 44M40 58 62 36M46 66 62 52M40 50 56 36"
        stroke={ILLU.wallStroke}
        strokeWidth={0.9}
      />
    </g>
    <path data-teil="kabel" d="M6 44h88" stroke={ILLU.steelFill} strokeWidth={4.4} strokeLinecap="round" />
    <path data-teil="kabel" d="M6 52h88" stroke={ILLU.steelFill} strokeWidth={4.4} strokeLinecap="round" />
    <path data-teil="kabel" d="M6 60h88" stroke={ILLU.steelFill} strokeWidth={4.4} strokeLinecap="round" />
    <path d="M6 43h88M6 51h88M6 59h88" stroke={ILLU.jointStroke} strokeWidth={1} />
    <rect x={38} y={38} width={24} height={28} fill="none" stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <rect x={64} y={70} width={14} height={10} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={1.1} />
    <path d="M67 74h8M67 77h5" stroke={ILLU.thatchTexture} strokeWidth={0.9} />
  </>
);

/**
 * Pulverwolke aus der Düse: nach unten breiter werdender Kegel aus vielen
 * kleinen Körnern. Ausgerechnet statt ausgewürfelt — die Zeichnung muss bei
 * jedem Rendern dieselbe sein.
 */
const PULVER_KOERNER = (() => {
  const koerner: { x: number; y: number; r: number }[] = [];
  for (let reihe = 0; reihe < 6; reihe += 1) {
    const y = 44 + reihe * 7.4;
    const halb = 4 + reihe * 3.4;
    const anzahl = 2 + reihe;
    for (let i = 0; i < anzahl; i += 1) {
      const t = anzahl === 1 ? 0.5 : i / (anzahl - 1);
      koerner.push({
        x: Number((68 - halb + t * halb * 2).toFixed(1)),
        y: Number((y + ((i + reihe) % 3) * 1.6).toFixed(1)),
        r: 1.1 + ((i + reihe) % 3) * 0.35,
      });
    }
  }
  return koerner;
})();

/**
 * Ortsfeste Pulverlöschanlage: Pulverbehälter, Treibgasflasche, Düse — und als
 * Löschmittel eine körnige Punktwolke. Punkte gegen Blasen ist der
 * Unterschied zur Schaumanlage, und er trägt auch ohne Farbwahrnehmung.
 */
const Pulverloeschanlagen = () => (
  <>
    <path
      data-teil="pulverbehaelter"
      d="M12 54a14 8 0 0 1 28 0v28a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6Z"
      fill={ILLU.plateFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
    <path d="M16 50h20" stroke={ILLU.steelFill} strokeWidth={2.4} strokeLinecap="round" />
    <path d="M14 70h26" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    <Flasche x={52} breite={6} hoehe={30} teil="treibgasflasche" />
    <path d="M52 53v-8h-12M26 46V30h42" stroke={ILLU.steelFill} strokeWidth={2.6} fill="none" strokeLinejoin="round" />
    <Duese x={68} y={30} />
    <g data-teil="punktwolke">
      {PULVER_KOERNER.map((k) => (
        <Punkt key={`${k.x}-${k.y}`} x={k.x} y={k.y} r={k.r} />
      ))}
    </g>
  </>
);

/**
 * Steigleitung: ein senkrechtes Rohr durch alle Geschosse, unten die
 * Einspeisung für die Feuerwehr, an jedem Geschoss eine Entnahmestelle. Rohr
 * ohne Schlauch — der Schlauch gehört zum Wandhydranten daneben.
 */
const Steigleitungen = () => {
  const geschosse = [22, 44, 66];
  return (
    <>
      {geschosse.map((y) => (
        <rect
          key={y}
          data-teil="geschossdecke"
          x={12}
          y={y}
          width={58}
          height={4}
          fill={ILLU.sockelFill}
          stroke={ILLU.wallStroke}
          strokeWidth={1.1}
        />
      ))}
      <path data-teil="steigleitung" d="M40 88V12" stroke={ILLU.steelFill} strokeWidth={5} strokeLinecap="round" />
      {geschosse.map((y) => (
        <g key={y} data-teil="entnahmestelle" transform={`translate(40 ${y + 12})`}>
          <path d="M0 0h12" stroke={ILLU.steelFill} strokeWidth={3} />
          <rect x={12} y={-4} width={5} height={8} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
          <circle cx={22} cy={0} r={4.5} fill="none" stroke={ILLU.roofStroke} strokeWidth={2} />
          <path d="M17 0h10M22 -5v10" stroke={ILLU.roofStroke} strokeWidth={1.4} />
        </g>
      ))}
      <g data-teil="einspeisung">
        <path d="M40 80H24" stroke={ILLU.steelFill} strokeWidth={3.4} />
        <rect x={14} y={73} width={10} height={14} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
        <circle cx={19} cy={80} r={3} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={0.9} />
      </g>
    </>
  );
};

/* ------------------------------------------ Motive: Elektrische Anlagen --- */

/**
 * Notbeleuchtung: Rettungszeichenleuchte über dem Ausgang und eine
 * Sicherheitsleuchte an der Decke, die den Fluchtweg ausleuchtet. Der Pfeil
 * zeigt die Richtung — das ist der Unterschied zu jeder anderen Leuchte.
 */
const Notbeleuchtung = () => (
  <>
    <Deckenlinie />
    <g data-teil="notleuchte">
      <rect x={12} y={14} width={18} height={7} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <path d="M21 14v-2" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    </g>
    <path
      data-teil="lichtkegel"
      d="M14 21 6 50h30Z"
      fill={ILLU.glassFill}
      stroke={ILLU.jointStroke}
      strokeWidth={1}
      strokeDasharray="3 3"
    />
    <Wandscheibe x={6} y={34} w={24} h={54} />
    <Wandscheibe x={70} y={34} w={24} h={54} />
    <rect x={30} y={34} width={40} height={8} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <rect data-teil="tueroeffnung" x={34} y={42} width={32} height={46} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <path d="M50 18v-6" stroke={ILLU.jointStroke} strokeWidth={1.4} />
    <g data-teil="rettungszeichen">
      <rect x={36} y={18} width={28} height={14} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.4} />
      <path data-teil="pfeil" d="M41 25h11M50 20l7 5-7 5Z" stroke={ILLU.roofStroke} strokeWidth={2} fill={ILLU.roofStroke} strokeLinejoin="round" />
    </g>
  </>
);

/**
 * Batterieladestation: zwei Batterieblöcke mit ihren Polen, darüber das
 * Ladegerät mit Anzeige, dazwischen die Ladekabel. Die Pole machen den Block
 * zur Batterie — ohne sie wäre es irgendein Kasten.
 */
const Batterieladestation = () => (
  <>
    <g data-teil="ladegeraet">
      <rect x={60} y={10} width={32} height={26} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
      <rect x={64} y={14} width={24} height={10} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      <circle cx={68} cy={31} r={2.4} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={0.8} />
      <circle cx={76} cy={31} r={2.4} fill={ILLU.sockelFill} />
      <circle cx={84} cy={31} r={2.4} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.8} />
    </g>
    <path
      data-teil="ladekabel"
      d="M70 36q-6 8-3 16"
      stroke={ILLU.roofStroke}
      strokeWidth={2.4}
      fill="none"
      strokeLinecap="round"
    />
    <path
      data-teil="ladekabel"
      d="M82 36q8 14-14 16t-42 0"
      stroke={ILLU.roofStroke}
      strokeWidth={2.4}
      fill="none"
      strokeLinecap="round"
    />
    <g data-teil="batterie">
      <rect x={12} y={56} width={28} height={32} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={16} y={52} width={6} height={4} fill={ILLU.steelFill} />
      <rect x={30} y={52} width={6} height={4} fill={ILLU.steelFill} />
      <path d="M21 56v32M31 56v32" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
    <g data-teil="batterie">
      <rect x={48} y={56} width={28} height={32} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <rect x={52} y={52} width={6} height={4} fill={ILLU.steelFill} />
      <rect x={66} y={52} width={6} height={4} fill={ILLU.steelFill} />
      <path d="M57 56v32M67 56v32" stroke={ILLU.jointStroke} strokeWidth={1} />
    </g>
  </>
);

/**
 * Kompressor für Lüftung und Druckluft: liegender Druckbehälter mit Motor,
 * Riemenscheibe und Manometer, die Druckluftleitung geht ins Lüftungsgitter.
 * Das Gitter ist der Teil „f. Lüftung" — ohne es wäre es nur ein Kompressor.
 */
const KompressorenLueftung = () => (
  <>
    <rect data-teil="druckbehaelter" x={8} y={54} width={56} height={22} rx={11} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <path d="M18 76v12M54 76v12" stroke={ILLU.steelFill} strokeWidth={3} strokeLinecap="round" />
    <rect data-teil="motor" x={22} y={34} width={26} height={18} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.4} />
    <path d="M27 34v18M33 34v18M39 34v18" stroke={ILLU.jointStroke} strokeWidth={1} />
    <circle cx={58} cy={43} r={8} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.4} />
    <circle cx={58} cy={43} r={2.6} fill={ILLU.steelFill} />
    <path d="M48 36 58 35M48 50 58 51" stroke={ILLU.jointStroke} strokeWidth={1.4} />
    <g data-teil="manometer">
      <circle cx={16} cy={44} r={6} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.3} />
      <path d="M16 44 19 40" stroke={ILLU.wallStroke} strokeWidth={1.2} strokeLinecap="round" />
      <path d="M16 50v6" stroke={ILLU.steelFill} strokeWidth={2} />
    </g>
    <path d="M64 62h18V36" stroke={ILLU.steelFill} strokeWidth={3} fill="none" strokeLinejoin="round" />
    <g data-teil="lueftungsgitter">
      <rect x={72} y={12} width={22} height={24} fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <path d="M74 17h18M74 22h18M74 27h18M74 32h18" stroke={ILLU.jointStroke} strokeWidth={1.4} />
    </g>
    <path d="M72 20h-8M72 28h-8" stroke={ILLU.jointStroke} strokeWidth={1.2} strokeDasharray="3 3" />
  </>
);

/**
 * Der Generatorsatz — bei Netzersatzanlage und Diesel-Notstromversorgung
 * BUCHSTÄBLICH derselbe, an denselben Koordinaten. Nur so liest sich das Paar
 * als „dieselbe Maschine, einmal mit Kraftstofftank": stünde die Maschine
 * verschoben, sähen die Kacheln verschieden aus, ohne verschieden zu sein.
 */
const Generatorsatz = () => (
  <>
    <rect data-teil="grundrahmen" x={8} y={74} width={58} height={7} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
    <path d="M14 81v7M58 81v7" stroke={ILLU.steelFill} strokeWidth={3} />
    <rect data-teil="motorblock" x={10} y={44} width={26} height={30} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <path d="M15 44v30M20 44v30M25 44v30M30 44v30" stroke={ILLU.jointStroke} strokeWidth={1} />
    <rect x={36} y={56} width={4} height={9} fill={ILLU.steelFill} />
    <rect data-teil="generator" x={40} y={46} width={24} height={28} rx={6} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
    <path d="M46 47v26M52 47v26M58 47v26" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    <rect x={45} y={38} width={14} height={8} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.1} />
    <path data-teil="abgasrohr" d="M20 44V20h10" stroke={ILLU.steelFill} strokeWidth={4} fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </>
);

/** Netzersatzanlage: Motor und Generator auf dem Grundrahmen — ohne Tank. */
const Netzersatzanlage = () => <Generatorsatz />;

/**
 * Notstromversorgung Netzersatzanlage (Diesel): derselbe Satz, dazu der
 * Kraftstofftank mit Füllstutzen und Schauglas und die Kraftstoffleitung zum
 * Motor. Der Kraftstoff IST der Unterschied — also wird er gezeichnet.
 */
const NetzersatzanlageDiesel = () => (
  <>
    <Generatorsatz />
    <path
      data-teil="kraftstoffleitung"
      d="M70 84H30v-10"
      stroke={ILLU.steelFill}
      strokeWidth={2.4}
      fill="none"
      strokeLinejoin="round"
    />
    <circle cx={50} cy={84} r={3.4} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    <rect data-teil="kraftstofftank" x={70} y={56} width={24} height={32} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <rect data-teil="fuellstutzen" x={77} y={50} width={9} height={6} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    <rect x={73} y={60} width={4} height={24} fill={ILLU.glassFill} stroke={ILLU.wallStroke} strokeWidth={0.9} />
    <path d="M73 68h4" stroke={ILLU.wallStroke} strokeWidth={1.2} />
    <path d="M82 64h9M82 72h9" stroke={ILLU.jointStroke} strokeWidth={1} />
  </>
);

/**
 * Fehlerstrom-Schutzeinrichtung: Schutzschalter auf der Hutschiene im
 * Verteiler, mit Kippschalter und Prüftaste — und darunter der
 * Summenstromwandler, der BEIDE Leiter umfasst. Der Ring um beide Leiter ist
 * das Prinzip: gemessen wird die Differenz, nicht der Strom.
 */
const FiSchutzeinrichtungen = () => (
  <>
    <rect data-teil="verteiler" x={12} y={14} width={76} height={58} fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth={1.6} />
    <path data-teil="leiter" d="M43 18v52" stroke={ILLU.steelFill} strokeWidth={3.4} />
    <path data-teil="leiter" d="M55 18v52" stroke={ILLU.steelFill} strokeWidth={3.4} />
    <path data-teil="hutschiene" d="M16 30h68" stroke={ILLU.sockelFill} strokeWidth={4} strokeLinecap="round" />
    <g data-teil="schutzschalter">
      <rect x={31} y={20} width={36} height={26} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1.5} />
      <g transform="rotate(-18 45 30)">
        <rect data-teil="kippschalter" x={40} y={23} width={10} height={14} rx={2} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1} />
      </g>
      <circle data-teil="pruefstaste" cx={59} cy={35} r={3.6} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={1} />
      <path d="M35 40h12" stroke={ILLU.jointStroke} strokeWidth={1.2} />
    </g>
    <ellipse
      data-teil="summenstromwandler"
      cx={49}
      cy={58}
      rx={14}
      ry={6.5}
      fill="none"
      stroke={ILLU.steelFill}
      strokeWidth={3}
    />
    <path d="M63 58h9v-12" stroke={ILLU.steelFill} strokeWidth={1.8} fill="none" strokeLinejoin="round" />
  </>
);

/**
 * Feststellanlage: dieselbe Brandschutztür — aber OFFEN, vom Haftmagneten
 * gehalten, überwacht vom Rauchmelder darüber, mit Handauslöser daneben. Ohne
 * Magnet und Melder wäre es nur eine offene Tür.
 */
const Feststellanlagen = () => (
  <>
    <Deckenlinie />
    <Melder x={40} />
    <Wandscheibe x={6} y={26} w={20} h={62} />
    <Wandscheibe x={74} y={26} w={20} h={62} />
    <path
      data-teil="tuerblattOffen"
      d="M30 26 58 32v56L30 88Z"
      fill={ILLU.wallFill}
      stroke={ILLU.wallStroke}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    <path d="M34 32v52" stroke={ILLU.jointStroke} strokeWidth={1} />
    <circle cx={54} cy={60} r={2.4} fill={ILLU.steelFill} />
    <rect data-teil="ankerplatte" x={58} y={52} width={5} height={9} fill={ILLU.plateFill} stroke={ILLU.wallStroke} strokeWidth={1} />
    <rect data-teil="haftmagnet" x={63} y={49} width={11} height={15} fill={ILLU.steelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
    <path
      data-teil="steuerleitung"
      d="M40 19v9h29v21"
      stroke={ILLU.steelFill}
      strokeWidth={1.8}
      fill="none"
      strokeLinejoin="round"
    />
    <g data-teil="handausloeser">
      <rect x={78} y={40} width={14} height={14} fill={ILLU.thatchFill} stroke={ILLU.thatchTexture} strokeWidth={1.2} />
      <circle cx={85} cy={47} r={3.4} fill={ILLU.gefachFill} stroke={ILLU.thatchTexture} strokeWidth={1} />
    </g>
  </>
);

/* ------------------------------------------------------------- Ersatzbild --- */

/**
 * Ersatzsymbol für einen Slug ohne Zeichnung. Bewusst neutral und als
 * Platzhalter erkennbar (gestrichelter Umriss, Fragezeichen-freier Kasten auf
 * dem Sockel): eine leere Kachel sähe nach Ladefehler aus, ein geliehenes
 * Motiv wäre eine Falschaussage. Es kommt nur zum Zug, wenn Katalog und
 * Bildsatz auseinanderlaufen — dann soll man das sehen.
 */
const Ersatzsymbol = () => (
  <>
    <rect
      data-teil="ersatzumriss"
      x={26}
      y={30}
      width={48}
      height={48}
      rx={4}
      fill={ILLU.headerBg}
      stroke={ILLU.jointStroke}
      strokeWidth={1.8}
      strokeDasharray="5 4"
    />
    <rect x={38} y={78} width={24} height={10} fill={ILLU.sockelFill} stroke={ILLU.wallStroke} strokeWidth={1.2} />
    <path d="M38 46h24M38 54h24M38 62h16" stroke={ILLU.jointStroke} strokeWidth={1.6} strokeLinecap="round" />
  </>
);

/* ------------------------------------------------------------- Registratur --- */

/**
 * Slug → Zeichnung. Die Schlüssel sind die Slugs aus `anlagenKatalog.ts`; sie
 * gehen in die Answer-Keys ein und dürfen sich nicht ändern.
 */
const MOTIVE: Record<string, () => JSX.Element> = {
  bma: Bma,
  ema: Ema,
  rwa: Rwa,
  sprinkler: Sprinkler,
  blitzschutz: Blitzschutz,
  rauchwarnmelder: Rauchwarnmelder,
  feuerloescher: Feuerloescher,
  brandschutztueren: Brandschutztueren,
  wandhydranten: Wandhydranten,
  funkenloeschanlagen: Funkenloeschanlagen,
  gasfeuerloeschanlagen: Gasfeuerloeschanlagen,
  stickstoffanlage: Stickstoffanlage,
  kleinloeschanlagen: Kleinloeschanlagen,
  schaumloeschanlagen: Schaumloeschanlagen,
  spruehwasserloeschanlagen: Spruehwasserloeschanlagen,
  entrauchungsanlagen: Entrauchungsanlagen,
  foerderanlagenabschluesse: Foerderanlagenabschluesse,
  brandschutzklappen: Brandschutzklappen,
  brandschottungen: Brandschottungen,
  pulverloeschanlagen: Pulverloeschanlagen,
  steigleitungen: Steigleitungen,
  notbeleuchtung: Notbeleuchtung,
  batterieladestation: Batterieladestation,
  kompressorenLueftung: KompressorenLueftung,
  netzersatzanlage: Netzersatzanlage,
  netzersatzanlageDiesel: NetzersatzanlageDiesel,
  fiSchutzeinrichtungen: FiSchutzeinrichtungen,
  feststellanlagen: Feststellanlagen,
};

/**
 * Wortlaut für `<title>` — beschreibt, was zu SEHEN ist, nicht was die Anlage
 * heißt (der Name steht als Text neben der Kachel). Bei den nah beieinander
 * liegenden Anlagen nennt er das Unterscheidungsmerkmal ausdrücklich, damit es
 * nicht am Bild allein hängt.
 */
const BESCHREIBUNG: Record<string, string> = {
  bma: "Brandmelderzentrale an der Wand, zwei Melder an der Decke, verbunden über die Meldelinie.",
  ema: "Bewegungsmelder mit Erfassungskegel, Fenster mit Öffnungskontakt und Außensirene.",
  rwa: "Öffnung in der Dachfläche mit aufgestellter Klappe, der Rauch zieht von selbst ab.",
  sprinkler: "Rohr unter der Decke mit drei Sprinklerköpfen; zwei tragen ihre Glasampulle, der ausgelöste sprüht.",
  blitzschutz: "Fangstange über dem First, Ableitung an der Gebäudekante, Erder unter der Geländeoberkante.",
  rauchwarnmelder: "Einzelner Melder an der Decke, der aufsteigenden Rauch erkennt und akustisch warnt.",
  feuerloescher: "Tragbarer Feuerlöscher im Wandhalter, mit Griffhebel, Manometer und Schlauch.",
  brandschutztueren: "Geschlossenes Türblatt in der Zarge, mit Drücker und obenliegendem Türschließer.",
  wandhydranten: "Wandschrank mit Sichtscheibe, darin Schlauchhaspel und Absperrventil.",
  funkenloeschanlagen: "Absaugrohr mit Funkenflug, Funkenmelder am Rohr und nachgeschalteter Löschdüse.",
  gasfeuerloeschanlagen: "Drei Gasflaschen am Sammelrohr, Düse im Raum, gestrichelte Gaswolke über dem Brand.",
  stickstoffanlage: "Eine Stickstoffflasche speist über ein Absperrventil einen geschlossenen Behälter mit Sauerstoff-Messfühler.",
  kleinloeschanlagen: "Kleiner Löschmittelbehälter mit genau einer Düse über genau einem Gerät.",
  schaumloeschanlagen: "Schaummittelbehälter, Zumischer und Schaumrohr; das Löschmittel liegt als großblasiger Schaum im Bild.",
  spruehwasserloeschanlagen: "Rohr mit vier offenen Düsen, die zugleich einen Tropfenfächer über ein Schutzobjekt legen.",
  entrauchungsanlagen: "Ventilator mit Motor im Abluftkanal, der den Rauch durch die Decke ins Freie zieht.",
  foerderanlagenabschluesse: "Förderband durch die Wandöffnung, darüber das Abschlussblatt in seinen Führungen.",
  brandschutzklappen: "Lüftungskanal durch die Wand, darin das Klappenblatt auf seiner Drehachse, darüber der Auslöser.",
  brandschottungen: "Wanddurchführung, mit Schottmasse verschlossen, durch die drei Kabel laufen.",
  pulverloeschanlagen: "Pulverbehälter mit Treibgasflasche und Düse; das Löschmittel tritt als körnige Punktwolke aus.",
  steigleitungen: "Senkrechtes Rohr über drei Geschosse, unten die Einspeisung, an jedem Geschoss eine Entnahmestelle.",
  notbeleuchtung: "Rettungszeichenleuchte mit Richtungspfeil über dem Ausgang und Sicherheitsleuchte an der Decke.",
  batterieladestation: "Zwei Batterieblöcke mit Polen, darüber das Ladegerät mit Anzeige und den Ladekabeln.",
  kompressorenLueftung: "Liegender Druckbehälter mit Motor und Manometer, die Druckluftleitung führt ins Lüftungsgitter.",
  netzersatzanlage: "Motor und Generator auf dem Grundrahmen, ohne Kraftstofftank.",
  netzersatzanlageDiesel: "Derselbe Generatorsatz, dazu der Kraftstofftank mit Füllstutzen und die Leitung zum Motor.",
  fiSchutzeinrichtungen: "Schutzschalter mit Prüftaste auf der Hutschiene, darunter der Summenstromwandler um beide Leiter.",
  feststellanlagen: "Offen stehendes Türblatt, vom Haftmagneten gehalten, überwacht vom Rauchmelder, daneben der Handauslöser.",
  ersatz: "Platzhalter — für diese Anlage ist noch keine Zeichnung hinterlegt.",
};

/** Schlüssel des Ersatzbildes. Kein Katalog-Slug, deshalb nicht in `MOTIVE`. */
const ERSATZ = "ersatz";

/* -------------------------------------------------------------- Komponente --- */

/** Der Slug aus `anlagenKatalog.ts`. */
export type AnlagenIconKey = string;

/** Die Slugs, für die eine eigene Zeichnung vorliegt — für Katalog-Abgleich und Tests. */
export const ANLAGEN_MIT_BILD: readonly string[] = Object.keys(MOTIVE);

/** Wortlaut für `<title>`; unbekannte Slugs bekommen den Platzhalter-Satz. */
export const anlagenBeschreibung = (anlage: AnlagenIconKey): string =>
  Object.prototype.hasOwnProperty.call(BESCHREIBUNG, anlage)
    ? BESCHREIBUNG[anlage]
    : BESCHREIBUNG[ERSATZ];

type Props = {
  /** Slug aus dem Katalog. Ein unbekannter Slug liefert das Ersatzsymbol. */
  anlage: AnlagenIconKey;
  /** Kantenlänge in px. Maße dürfen auch per CSS kommen; das hier ist der Fallback. */
  size?: number;
  className?: string;
};

/**
 * Ein Anlagensymbol.
 *
 * Rein dekorativ (`aria-hidden` im SVG selbst): in der Kachel steht die
 * Beschriftung direkt daneben und sagt dasselbe — dieselbe Abwägung wie bei
 * `AusstattungIcon`. Das `<title>` bleibt trotzdem stehen: es ist der Tooltip
 * beim Hovern und liegt unter `aria-hidden` nicht im Vorlesenamen.
 *
 * `hasOwnProperty` statt `MOTIVE[anlage]`: sonst lieferte ein Slug wie
 * `toString` oder `constructor` die Prototyp-Eigenschaft und würde als
 * Komponente gerendert.
 */
export const AnlagenIcon = ({ anlage, size = 64, className }: Props): JSX.Element => {
  const bekannt = Object.prototype.hasOwnProperty.call(MOTIVE, anlage);
  const Motiv = bekannt ? MOTIVE[anlage] : Ersatzsymbol;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${KANTE} ${KANTE}`}
      fill="none"
      data-anlage={bekannt ? anlage : ERSATZ}
      aria-hidden="true"
      focusable="false"
    >
      <title>{anlagenBeschreibung(bekannt ? anlage : ERSATZ)}</title>
      <Grundlinie />
      <Motiv />
    </svg>
  );
};
